<script>
/**
 * ****************************************************************************
 * @description     :   Input Component
 * @created at      :   2024/05/22
 * @created by      :   ANS903 – phuongdt@ans-asia.com
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

/**
 * Import template
 */
import helpers from '@/utils/helpers';
import template from './template.html';
import * as AutoKana from 'vanilla-autokana';

/**
 * Define content of input component
 */
var PTJAnsInput = {
    template: template,
    props: {
        id: String,
        label: String,
        style: String,
        itemClass: String,
        buttonClass: String,
        buttonSecondClass: String,
        labelClass: String,
        labelBefore: String,
        labelAfter: String,
        inputClass: String,
        placeholder: String,
        value: String,
        modelValue: String | Number,
        isRequired: Boolean,
        isDisabled: Boolean,
        isDisabledIcon: Boolean,
        isDisabledSecondIcon: Boolean,
        isReadonly: Boolean,
        isMobile: {
            type: Boolean,
            default: false
        },
        isAlwaysEnable: {
            type: Boolean,
            default: false
        },
        type: String,
        isDecimal: {
            type: Boolean,
            default: false
        },
        isEnableIME: {
            type: Boolean,
            default: false
        },
        isAddress: {
            type: Boolean,
            default: false
        },
        isJpFurigana: {
            type: Boolean,
            default: false
        },
        isAutoKana: {
            type: Array,
            default: []
        },
        decimal: Number,
        isNegative: Boolean,
        isNotShowCurrency: Boolean,
        maxLength: Number,
        cols: Number,
        rows: Number,
        icon: String,
        secondIcon: String,
        buttonLabel: {
            type: String,
            default: ''
        },
        secondButtonLabel: {
            type: String,
            default: ''
        },
        isInputGroup: Boolean,
        isSecondInputGroup: Boolean,
        onChange: Function,
        onFocus: Function,
        onBlur: Function,
        onClick: Function,
        onSecondClick: Function,
        isStartWithZero: {
            type: Boolean,
            default: true
        },
        isUppercase: {
            type: Boolean,
            default: false
        },
        isHorizontal: {
            type: Boolean,
            default: false
        },
        appendBtn: {
            type: String,
            default: ''
        },
        labelAfterClass: {
            type: String,
            default: ''
        },
        inputGroupClass: {
            type: String,
            default: ''
        },
        errors: {
            type: Array
        },
        checkValidateInputPassword: {
            type: Boolean,
            default: false
        },
        isAutocomplete: {
            type: Boolean,
            default: false
        },
        tabindex: {
            type: Number,
            default: 1
        }
    },
    emits: ['update:modelValue', 'referKana', 'validateSuccess'],
    data() {
        return {
            isFocus: false,
            realMaxLength: undefined,
            autoKana: undefined,
            modeDisabled: false,
            hasErrorMessage: false,
            inputChangeKana: false
        };
    },
    mounted() {
        this.realMaxLength = this.getRealMaxLength();
        if (this.isAutoKana && this.isAutoKana.length > 1) {
            this.$nextTick(() => {
                this.initializeAutoKana();
            });
        }
        if (
            ![
                'CUS_Login',
                'MNG_Login',
                'App_Login',
                'CUS_System901',
                'CUS_System902',
                'MNG_System901',
                'MNG_System902',
                'MNG_Order001',
                'MNG_System002',
                'MNG_System004',
                'MNG_Order004',
                'MNG_File001',
                'CUS_Order021',
                'CUS_Billing001',
                'CUS_Client003',
                'CUS_Delivery001',
                'CUS_System004'
            ].includes(this.$route.name)
        ) {
            this.modeDisabled = helpers.checkAuthorityMode(this.$route.name) && !this.isAlwaysEnable;
        }
    },
    computed: {
        vewValue: {
            get() {
                let val = this.convertNumber(this.modelValue ? this.modelValue : this.value);
                if (this.isFocus && !this.isDisabled && !this.isReadonly) {
                    if (this.type == 'money') {
                        val = val.replace(/¥/g, '');
                    }
                    // eslint-disable-next-line prettier/prettier
                    return this.type == 'money' || (this.type == 'measure' && !this.isDecimal)
                        ? val.replace(/,/g, '')
                        : this.type == 'postcode'
                        ? val.replace(/-/g, '')
                        : val;
                }
                return val;
            },
            set() {}
        },
        renderErrorMsg() {
            let message = '';
            this.hasErrorMessage = false;
            if (this.errors) {
                this.errors.forEach((item) => {
                    if (item.name == this.id) {
                        message = item.message;
                        this.hasErrorMessage = true;
                    }
                });
            }
            return message;
        },
        validateInputPassword() {
            if (this.checkValidateInputPassword) {
                if (this.modelValue && !this.validateHalfSizeAlphanumeric(this.modelValue)) {
                    return false;
                } else {
                    return true;
                }
            } else {
                return true;
            }
        },
        checkRequire() {
            return !!(this.vewValue.trim() && this.validateInputPassword && !this.renderErrorMsg);
        }
    },
    methods: {
        /**
         * initialize autokana
         * -----------------------------------------
         * @author : ManhND - 2024/07/09 - create
         * @params :
         * @access : private
         */
        initializeAutoKana() {
            const [kanaInput, kanaOuput] = this.isAutoKana;
            this.autoKana = AutoKana.bind(kanaInput, kanaOuput, {
                katakana: true
            });
            this.autoKana.stop();
        },
        /**
         * convert number type for input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        convertNumber(value) {
            if (value == undefined || value == '' || value == null) {
                return '';
            }
            value = value + '';
            let values = [];
            let negative = '';
            let dot = '';
            let afterDot = '';
            if (this.type == 'number' || this.type == 'money' || this.type == 'measure') {
                if (this.isDecimal) {
                    this.realMaxLength = this.maxLength + 1;
                }

                if (this.isNegative) {
                    negative = '-';
                }
                values = value.split('.');
                values[0] = values[0].replace(/\D/g, '');

                if (values.length > 1) {
                    afterDot = values[1].replace(/\D/g, '');
                    if (afterDot.length > this.decimal) {
                        afterDot = afterDot.substring(0, this.decimal);
                    }
                    dot = '.';
                    this.isDecimal ? (value = negative + values[0] + dot + afterDot) : (value = negative + values[0]);
                }

                if (this.type == 'measure' && !this.isDecimal) {
                    values[0] = values[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    value = negative + values[0];
                }

                if (this.type == 'money') {
                    values[0] = values[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    if (values[0].length > 1) {
                        values[0] = values[0].replace(/^0+/, '');
                    }
                    if (values[0]) {
                        value = negative + (this.isNotShowCurrency ? '' : '¥') + values[0] + (values.length > 1 ? '.' + values[1] : '');
                    } else {
                        value = negative + (this.isNotShowCurrency ? '' : '¥') + values[0] + (values.length > 1 ? '.' + values[1] : '');
                    }
                }
                // author: trinhdtk - ANS845
                // if type is number, and not allow start with zero
                // Eg: 0123 => 123
                if (this.type == 'number' && !this.isStartWithZero) {
                    values[0] = values[0].replace(/^0+/, '');
                    value = values[0];
                }

                if (this.isDecimal && this.decimal > 0 && afterDot === '' && value.length > this.maxLength - this.decimal) {
                    dot = '.';
                    afterDot = values[0].substring(this.maxLength - this.decimal, value.length);
                    values[0] = values[0].substring(0, this.maxLength - this.decimal);
                    this.isDecimal ? (value = negative + values[0] + dot + afterDot) : (value = negative + values[0]);
                }

                if (!values[0]) {
                    values[0] = 0;
                }
            }

            if (this.type == 'postcode') {
                const postcode = value.replace(/\D/g, '');
                if (postcode.length >= 7) {
                    value = postcode.slice(0, 3) + '-' + postcode.slice(3, 7);
                } else {
                    value = '';
                }
            }
            return value;
        },
        /**
         * get max lenght input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        getRealMaxLength(e) {
            if (this.maxLength == undefined || this.maxLength == null || this.type != 'money' || this.type != 'measure') {
                let maxLength = this.maxLength;
                if (e) {
                    if (this.type == 'number') {
                        if (e.target.value.indexOf('-') >= 0) {
                            maxLength += 1;
                        }
                        if (e.target.value.indexOf('.') >= 0) {
                            maxLength += 1;
                        }
                    }
                    if (this.type == 'postcode') {
                        if (e.target.value.indexOf('-') >= 0) {
                            maxLength += 1;
                        }
                    }
                }
                return maxLength;
            }
            return this.maxLength + (parseInt(this.maxLength / 3) + (this.maxLength % 3 > 0 ? 0 : -1));
        },
        /**
         * event forcus on input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        focusInput(e) {
            //e.target.select();
            this.$refs.input.focus();
            this.isFocus = true;
            this.realMaxLength = this.maxLength;
            if (this.onFocus) {
                this.onFocus(e);
            }
        },
        /**
         * event blur on input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        blurInput(e) {
            this.isFocus = false;
            this.realMaxLength = this.getRealMaxLength(e);
            if (this.type == 'tel') {
                this.validateTel(e.target.value, e.target);
            }
            if (this.autoKana && this.inputChangeKana) {
                this.validateInputAutoKana(this.autoKana.elName);
                this.$emit('referKana', this.autoKana);
            }
            if (this.checkValidateInputPassword) {
                let status = true;
                if (this.modelValue && !this.validateHalfSizeAlphanumeric(this.modelValue)) {
                    status = false;
                }
                this.$emit('validateSuccess', status);
            }
            if (this.onBlur) {
                this.onBlur(e);
            }
        },
        /**
         * validate only accept japanese's character, number, and special character when blur
         * -----------------------------------------
         * @author : ManhND - 2024/07/12 - create
         * @params :
         * @access : private
         */
        validateInputAutoKana(itemAutoKana) {
            const string = itemAutoKana.value;
            const japaneseRegex = /^[\u3040-\u30FF\u4E00-\u9FAF0-9\s\p{P}\p{S}]*$/u;
            if (!japaneseRegex.test(string)) {
                // itemAutoKana.value = '';
                // itemFurigana.value = '';
                this.updateInput();
            }
        },
        /**
         * validate tel when blur
         * -----------------------------------------
         * @author : ManhND - 2024/07/12 - create
         * @params :
         * @access : private
         */
        validateTel(value, input) {
            let tel = '';
            const regexOnlyNumber = /^\d+$/;
            const regexTel11 = /^\d{3}-\d{4}-\d{4}$/;
            const regexTel10 = /^\d{2}-\d{4}-\d{4}$/;
            const string = value.replace(/\D/g, '');
            if (regexOnlyNumber.test(string)) {
                if (string.length == 11) {
                    tel = `${string.slice(0, 3)}-${string.slice(3, 7)}-${string.slice(7, 11)}`;
                } else if (string.length == 10) {
                    tel = `${string.slice(0, 2)}-${string.slice(2, 6)}-${string.slice(6, 10)}`;
                }
            } else {
                if (regexTel11.test(string) || regexTel10.test(string)) {
                    tel = string;
                }
            }
            input.value = tel;
            this.$emit('update:modelValue', tel);
        },
        /**
         * event keydown on input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        inputKeydown(e) {
            const keyCode = e.keyCode || e.which;
            const keyCodeSpecial =
                keyCode === 35 || // end
                keyCode === 36 || // home
                keyCode === 116 || // f5
                keyCode === 37 || // arrow left
                keyCode === 38 || // arrow up
                keyCode === 39 || // arrow right
                keyCode === 40 || // arrow down
                // keyCode === 8 || // backspace
                keyCode === 9; //tab
            if (!keyCodeSpecial) {
                if (this.autoKana != undefined) {
                    this.inputChangeKana = true;
                    this.autoKana.start();
                }
            }
            if (this.type == 'number' && this.isDecimal) {
                return this.inputOnlyDecimal(e);
            }
            if (this.type == 'number' || this.type == 'money' || this.type == 'measure' || this.type == 'postcode') {
                return this.inputOnlyNumber(e);
            }
            if (this.type == 'tel') {
                return this.inputOnlyTel(e);
            }
            // if (this.isAutoKana.length > 0) {
            //     const keyCode = e.keyCode || e.which;
            //     if (keyCode === 9) {
            //         e.preventDefault();
            //         return false;
            //     }
            // }
            if (this.type === 'upperAlphanumeric') {
                const keyCodeCommon =
                    keyCode === 35 || // end
                    keyCode === 36 || // home
                    keyCode === 116 || // f5
                    keyCode === 37 || // arrow left
                    keyCode === 39 || // arrow right
                    keyCode === 8 || // backspace
                    keyCode === 9; //tab
                const alphaNumeric =
                    (keyCode >= 65 && keyCode <= 90) || // a-z
                    (keyCode >= 48 && keyCode <= 57) || // 0-9
                    (keyCode >= 96 && keyCode <= 105); // numpad 0-9)

                // prevent
                if (
                    (e.shiftKey && keyCode >= 48 && keyCode <= 57) || // 0-9
                    !(alphaNumeric || keyCodeCommon)
                ) {
                    e.preventDefault();
                    return false;
                }
            }
        },
        inputKeyup(e) {
            let maxLength = this.maxLength;
            if (this.type == 'number') {
                if (e.target.value.indexOf('-') >= 0) {
                    maxLength += 1;
                }
                if (e.target.value.indexOf('.') >= 0) {
                    maxLength += 1;
                }
            }
            if (this.type == 'postcode') {
                if (e.target.value.indexOf('-') >= 0) {
                    maxLength += 1;
                }
            }
            if (this.type == 'measure' && this.isDecimal) {
                if (e.target.value.indexOf('.') >= 0) {
                    maxLength += 1;
                }
            }
            this.realMaxLength = maxLength;
            if (this.type == 'postcode' && this.realMaxLength != e.target.value.length) {
                return false;
            }
            if (this.isUppercase) {
                this.$emit('update:modelValue', e.target.value.toUpperCase());
            } else {
                this.$emit('update:modelValue', e.target.value);
            }
            return true;
        },
        /**
         * event block key on input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        inputOnlyNumber(e) {
            if (
                !(
                    (e.keyCode > 47 && e.keyCode < 58) ||
                    (e.keyCode > 95 && e.keyCode < 106) ||
                    e.keyCode == 116 ||
                    e.keyCode == 46 ||
                    e.keyCode == 37 ||
                    e.keyCode == 39 ||
                    e.keyCode == 35 ||
                    e.keyCode == 36 ||
                    e.keyCode == 8 ||
                    e.keyCode == 9 ||
                    e.ctrlKey ||
                    e.keyCode == 229 ||
                    e.keyCode == 13 ||
                    e.keyCode == 189 ||
                    e.keyCode == 109
                ) ||
                (!this.isNegative && (e.keyCode == 189 || e.keyCode == 109))
            ) {
                event.preventDefault();
                return false;
            }
            return true;
        },
        /**
         * event block key on input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        inputOnlyDecimal(e) {
            if (
                !(
                    (e.keyCode > 47 && e.keyCode < 58) ||
                    (e.keyCode > 95 && e.keyCode < 106) ||
                    e.keyCode == 116 ||
                    e.keyCode == 46 ||
                    e.keyCode == 37 ||
                    e.keyCode == 39 ||
                    e.keyCode == 35 ||
                    e.keyCode == 36 ||
                    e.keyCode == 8 ||
                    e.keyCode == 9 ||
                    e.ctrlKey ||
                    e.keyCode == 229 ||
                    e.keyCode == 110 ||
                    e.keyCode == 190 ||
                    e.keyCode == 13
                ) ||
                (!this.isNegative && e.keyCode == 189) ||
                e.keyCode == 109
            ) {
                event.preventDefault();
                return false;
            }
            return true;
        },
        /**
         * event block key on input with type tel
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        inputOnlyTel(e) {
            if (
                !(
                    (!e.shiftKey && e.keyCode > 47 && e.keyCode < 58) ||
                    (!e.shiftKey && e.keyCode > 95 && e.keyCode < 106) ||
                    e.keyCode == 116 ||
                    e.keyCode == 46 ||
                    e.keyCode == 37 ||
                    e.keyCode == 39 ||
                    e.keyCode == 35 ||
                    e.keyCode == 36 ||
                    e.keyCode == 8 ||
                    e.keyCode == 9 ||
                    e.ctrlKey ||
                    e.keyCode == 229 ||
                    // e.keyCode == 107 ||
                    e.keyCode == 109 ||
                    (!e.shiftKey && e.keyCode == 189) ||
                    (e.shiftKey && e.keyCode == 57) ||
                    (e.shiftKey && e.keyCode == 48) ||
                    (e.shiftKey && e.keyCode == 187)
                )
            ) {
                event.preventDefault();
                return false;
            }
            return true;
        },
        /**
         * update input
         * -----------------------------------------
         * @author : PhuongDT - 2024/05/22 - create
         * @params :
         * @access : private
         */
        updateInput(e) {
            if (this.autoKana != undefined) {
                if (this.inputChangeKana) {
                    this.$emit('referKana', this.autoKana);
                    this.inputChangeKana = false;
                }
                this.autoKana.stop();
            }
            const val = this.convertNumber(this?.$refs?.input?.value);
            let value = this.type == 'money' || (this.type == 'measure' && !this.isDecimal) ? val.replace(/,/g, '') : val;
            if (this.type == 'money' || this.type == 'number') {
                let regex = '';
                if (this.isNegative) {
                    regex = this.isDecimal ? /([^0-9/.-])/g : /[^0-9/-]/g;
                } else {
                    regex = this.isDecimal ? /([^0-9/.])/g : /[^0-9]/g;
                }
                value = value.replace(regex, '');
                if (!value) e.target.value = '';
            }
            if (!this.isAddress) {
                if (!this.isEnableIME) {
                    const regex = /[一-龠ぁ-ゔァ-ヴーａ-ｚＡ-Ｚ０-９々〆〤]+/u;
                    value = value.replace(regex, '');
                } else {
                    const regex = /[ｧ-ﾝﾞﾟ]+/u;
                    value = value.replace(regex, '');
                }
            }
            if (this.type == 'measure' && this.isDecimal) {
                const values = val.split('.');
                let dot = '';
                let afterDot = '';
                if (values.length > 1) {
                    afterDot = values[1].replace(/\D/g, '');
                    if (afterDot.length > this.decimal) {
                        afterDot = afterDot.substring(0, this.decimal);
                    }
                    dot = '.';
                }
                value = values[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + dot + afterDot;
            }
            if (this.type === 'upperAlphanumeric') {
                value = value.replace(/[^A-Za-z0-9]/g, '');
            }
            if (this.isJpFurigana) {
                const newValue = val.replace(/[\u200B-\u200D\uFEFF]/g, '');
                // \u0020: half-width space
                // \u3000: full-width space
                const regex = /^([ァ-ヴー]|ー|\u0020|\u3000)+$/;
                if (regex.test(newValue)) {
                    value = newValue;
                } else {
                    value = '';
                }
            }
            this.$emit('update:modelValue', value);
            if (this.onChange) {
                this.onChange(e, value);
                helpers.handleAddRemoveClass([
                    {
                        ele: e.target,
                        arrAddRemove: ['require-input'],
                        type: 0
                    }
                ]);
            }
        },
        validateHalfSizeAlphanumeric(string) {
            var regexp = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[#?!@$%^&*-])[A-Za-z\d#?!@$%^&*-]{10,}$/;
            if (regexp.test(string) || string == '') {
                return true;
            } else {
                return false;
            }
        }
    }
};
export default PTJAnsInput;
</script>
